<script setup lang="ts">
import SiteSection from '@/client/components/SiteSection.vue';
import { isEmbeddableMap, mapUrl } from '@/client/maps';
import type { ContactsContent } from '@/client/types';
import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-vue-next';
import { computed } from 'vue';

const props = defineProps<{
    id: string;
    content: ContactsContent;
}>();

/**
 * An embed address shows the map in place; any other map link becomes a button.
 */
const map = computed(() => (props.content.map_url ? mapUrl(props.content.map_url) : null));
const embedded = computed(() => map.value !== null && isEmbeddableMap(map.value));
</script>

<template>
    <SiteSection :id="id" :title="content.title">
        <div :class="['grid gap-10', { 'md:grid-cols-2': embedded }]">
            <dl class="grid content-start gap-6">
                <div v-if="content.address">
                    <dt class="text-sm text-muted-foreground">{{ $t('site.address') }}</dt>
                    <dd class="mt-1 text-lg whitespace-pre-line">{{ content.address }}</dd>
                </div>
                <div v-if="content.phone">
                    <dt class="text-sm text-muted-foreground">{{ $t('site.phone') }}</dt>
                    <dd class="mt-1 text-lg">
                        <a :href="`tel:${content.phone}`" class="underline-offset-4 hover:underline">{{ content.phone }}</a>
                    </dd>
                </div>
                <div v-if="content.hours">
                    <dt class="text-sm text-muted-foreground">{{ $t('site.hours') }}</dt>
                    <dd class="mt-1 text-lg whitespace-pre-line">{{ content.hours }}</dd>
                </div>
                <div v-if="content.instagram || content.telegram" class="flex gap-4">
                    <a v-if="content.instagram" :href="content.instagram" target="_blank" rel="noopener" class="underline-offset-4 hover:underline">Instagram</a>
                    <a v-if="content.telegram" :href="content.telegram" target="_blank" rel="noopener" class="underline-offset-4 hover:underline">Telegram</a>
                </div>
                <div v-if="map && !embedded">
                    <Button variant="outline" as-child>
                        <a :href="map" target="_blank" rel="noopener">
                            <MapPin />
                            {{ $t('site.open_map') }}
                        </a>
                    </Button>
                </div>
            </dl>
            <iframe
                v-if="map && embedded"
                :src="map"
                :title="content.title ?? ''"
                class="aspect-[4/3] w-full rounded-2xl border"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
            />
        </div>
    </SiteSection>
</template>
